/**
 * Strict design facts extracted from FFU / drawings and resolved at batch
 * merge. The stored tree is the resolved product: one value per leaf.
 * Null only when no extractor produced a candidate. Candidate lists are
 * merge input only — never persist them on the tree.
 */

import { z } from 'zod';
import { parsePlanNumbers } from '../helpers/extracted-storey-key.helpers';
import {
  aiStoreyKindSchema,
  storeyCountSchema,
  storeyLevelSchema,
} from './storey-level.model';

const nullableStr = (description: string) =>
  z.nullable(z.string()).describe(description);

const nullableNum = (description: string) =>
  z.nullable(z.number()).describe(description);

/**
 * Glazed facade fraction the extractors may emit. A printed fönsterandel or
 * glastäckning, a quotient of printed glass and facade areas, or one estimate
 * from a facade, elevation, or rendering.
 */
export const WINDOW_RATIO_VALUE_DESCRIPTION =
  'Glazed fraction of the facade, 0–1. Printed fönsterandel or glastäckning, the quotient of a printed glass area and a printed facade area, or one estimate from a fasad, elevation, or rendering. Not window-to-floor. Not BOA, BTA, or a parking percentage. Null when none of those is available.';

/** How {@link WINDOW_RATIO_VALUE_DESCRIPTION} was determined. */
export const WINDOW_RATIO_DETERMINED_BY = [
  'read',
  'calculated',
  'estimated',
] as const;

export type WindowRatioDeterminedBy =
  (typeof WINDOW_RATIO_DETERMINED_BY)[number];

/**
 * Narrow companion to {@link WINDOW_RATIO_VALUE_DESCRIPTION}.
 * `read` is printed. `calculated` is a quotient of printed areas.
 * `estimated` is from a facade, elevation, or rendering.
 * Null when `window_ratio` is null.
 */
export const WINDOW_RATIO_DETERMINED_BY_DESCRIPTION =
  'How window_ratio was determined. read: a printed fönsterandel or glastäckning. calculated: the quotient of a printed glass area and a printed facade area. estimated: one fraction from a facade, elevation, or rendering. Null when window_ratio is null.';

/** Mapped program types. `other` is the only type that keeps a printed label. */
export const MAPPED_EXTRACTED_PROGRAM_TYPES = [
  'residential',
  'office',
  'industry',
  'retail',
  'school',
  'garage',
  'stairwells',
] as const;

export const EXTRACTED_PROGRAM_TYPES = [
  ...MAPPED_EXTRACTED_PROGRAM_TYPES,
  'other',
] as const;

export const EXTRACTED_PROGRAM_UNSTATED_USE_GUIDANCE =
  'Where the documents do not show or state the use of a space or a storey, it gets the use most usual at that place in that type of building; it is `other` only when that use is none of the types above.';

/** Swedish words for the program types. */
export const EXTRACTED_PROGRAM_TYPE_SV =
  'SV: bostad→residential, kontor→office, industri→industry, handel→retail, skola→school, garage→garage, trapphus→stairwells.';

export const EXTRACTED_PROGRAM_TYPE_DESCRIPTION =
  `Program type. ${EXTRACTED_PROGRAM_TYPE_SV} Anything that does not map is other; keep the printed word in label or zone. Handel or retail when the row says so. ` +
  EXTRACTED_PROGRAM_UNSTATED_USE_GUIDANCE;

/** Resolved design facts and final FFU output. */
export const EXTRACTED_PROGRAM_SHARE_NODON_DESCRIPTION =
  'Nodon program share on this storey (0–1): this program divided by the sum of editable Nodon program areas on the same storey (mapped types such as residential, office, retail — not `other`, not `stairwells`). `other` rows are excluded from the denominator; Nodon calculates the stairwell area from the apartments, so a `stairwells` row has share null. When the building is described in named parts, the storey is the storey of that part: the denominator holds only the programs of that part on that floor, never another part printed on the same sheet or in the same floor block. A part whose floor has one mapped program has share 1 for it. Set share when stated areas or a printed andel support it; null when unknown.';

/** Segment areatabell / blueprint plan extraction: Nodon share at the earliest step. */
export const EXTRACTED_PROGRAM_SHARE_SEGMENT_DESCRIPTION = `${EXTRACTED_PROGRAM_SHARE_NODON_DESCRIPTION} When the table or plan prints an andel or programandel for this row, copy that value as share instead of computing. When not printed, compute from stated area_m2 on mapped-program rows in the same floor block and the same part (same printed floor in areas_by_zone, or same storey_index and part_name in programs).`;

export const EXTRACTED_PROGRAM_LABEL_DESCRIPTION =
  'The word the document prints for this program when type is other. Null when the type is mapped, and null when no word is printed.';

export type ExtractedProgramType = (typeof EXTRACTED_PROGRAM_TYPES)[number];

export const extractedProgramTypeSchema = z.enum(EXTRACTED_PROGRAM_TYPES);

export const EXTRACTED_AREA_KINDS = [
  'bta',
  'ljus_bta',
  'mork_bta',
  'boa',
  'loa',
  'bya',
  'bra',
  'unknown',
] as const;

export type ExtractedAreaKind = (typeof EXTRACTED_AREA_KINDS)[number];

export const extractedAreaKindSchema = z.enum(EXTRACTED_AREA_KINDS);

export const EXTRACTED_DESIGN_FACT_SOURCES = [
  'area_schedule',
  'technical_spec',
  'project_brief',
  'project_description',
  'title_block',
  'blueprint_side_view',
  'blueprint_top_view',
  'blueprint',
  'joined_blueprint',
  'building_rendering',
] as const;

export type ExtractedDesignFactSource =
  (typeof EXTRACTED_DESIGN_FACT_SOURCES)[number];

export const extractedDesignFactSourceSchema = z.enum(
  EXTRACTED_DESIGN_FACT_SOURCES,
);

/**
 * Leaf kinds that share a source ranking. Used by
 * {@link pickMostLikelyExtractedDesignFact} — not persisted.
 */
export const EXTRACTED_DESIGN_FACT_LEAF_KINDS = [
  'bta_m2',
  'area_m2',
  'programs',
  'storey_count',
  'storey_index',
  'inner_height_m',
  'floor_to_floor_height_m',
  'window_ratio',
  'beam_span_m',
  'apartment_size',
  'balcony',
  'bathroom',
  'program_type',
  'stairwells',
  'area_total',
  'other_area',
  'apartment_count',
  'apartment',
  'project_name',
  'location',
  'property_designation',
  'detailed_plan',
  'client',
  'building_name',
  'building_count',
  'roof_form',
  'slab_thickness_mm',
] as const;

export type ExtractedDesignFactLeafKind =
  (typeof EXTRACTED_DESIGN_FACT_LEAF_KINDS)[number];

export const extractedDesignFactLeafKindSchema = z.enum(
  EXTRACTED_DESIGN_FACT_LEAF_KINDS,
);

const extractedProgramAiSchema = z.object({
  type: extractedProgramTypeSchema.describe(EXTRACTED_PROGRAM_TYPE_DESCRIPTION),
  area_m2: nullableNum('Program area on this storey in m²; null if unknown.'),
  share: nullableNum(EXTRACTED_PROGRAM_SHARE_NODON_DESCRIPTION),
  label: nullableStr(EXTRACTED_PROGRAM_LABEL_DESCRIPTION),
});

const extractedStoreyAiSchema = z.object({
  index: nullableNum(
    '0 on the entrance, negative below, positive above, in the numbering the documents use. Still set this when the entrance sheet is not in the upload. Null when the storey cannot be placed.',
  ),
  label: nullableStr(
    'Plan name. `Plan N` when a plan number is printed. Do not replace that name with entréplan. An apartment type such as 1 RoK is not a storey — omit it. Null when no plan name is printed.',
  ),
  storey_kind: aiStoreyKindSchema.describe(
    'regular when this storey has its own plan. typical only for a normalplan/typplan that fills storeys with no own sheet — not for a numbered plan. attic for vind/vindsplan. technical for a teknikvåning or fläktrum storey on top whose programme areas a plan shows, also on the roof. mezzanine for entresol. split_level for a suterräng storey partly below grade. roof for a takplan that shows only the roof, with no programme area, above every storey.',
  ),
  is_ground_floor: z
    .boolean()
    .nullable()
    .describe(
      'True only when the documents identify this storey as the entrance. Do not infer the entrance as one below the next numbered plan. False when this storey is not the entrance. Null when it cannot be placed.',
    ),
  inner_height_m: nullableNum(
    "Room height in metres printed for this storey, including the uppermost storey and a technical storey. A use label on the storey does not make this null. Null only when this storey prints no room height. Not a structural floor height. When the text names more than one part, this is the room height printed for this part. Do not copy another part's room height onto this row.",
  ),
  floor_to_floor_height_m: nullableNum(
    'Structural floor height in metres printed for this storey (floor-to-floor, bjälklagshöjd, or våningshöjd), including the uppermost dimensioned storey. A title storey count does not drop a height this storey prints. Null only when this storey prints none. Not rumshöjd.',
  ),
  area_m2: nullableNum('This storey BTA/GFA in m²; null if unknown.'),
  programs: z
    .array(extractedProgramAiSchema)
    .describe(
      'Uses of this storey, one row per use. A use that does not map is other, and label is the use stated for the storey. When several labelled areas belong to that use, each stays in other_areas with its own area. Do not open a program row for each of them, and do not replace the use with one of their names. Empty when the storey states no use.',
    ),
});

/** Residential form the documents state. */
export const RESIDENTIAL_PROGRAM_FORM = [
  'single_family',
  'multifamily',
  'special_purpose',
] as const;

export type ResidentialProgramForm = (typeof RESIDENTIAL_PROGRAM_FORM)[number];

const statedPerApartment = (what: string, total: string): string =>
  `${what} in m². The printed per-apartment figure, or the quotient of a printed ${total} and a printed apartment count when the per-apartment figure is not printed. Null when those stated numbers are missing. Not a typical size.`;

export const extractedResidentialAiSchema = z.object({
  living_area_per_apartment_m2: nullableNum(
    statedPerApartment('Living area per apartment', 'living-area total'),
  ),
  balcony_area_per_apartment_m2: nullableNum(
    statedPerApartment('Balcony area per apartment', 'balcony-area total'),
  ),
  bathroom_area_per_apartment_m2: nullableNum(
    "Bathroom area per apartment in m². The printed area, or the product of a printed room's sides when the area is not printed. Null when the document does not state the room. Not a typical size.",
  ),
  program_type: z
    .enum(RESIDENTIAL_PROGRAM_FORM)
    .nullable()
    .describe(
      'Residential form the document states: single_family, multifamily, or special_purpose. Null when the document does not state the form.',
    ),
});

export const extractedStairwellsAiSchema = z.object({
  apartments_per_stairwell_per_storey: nullableNum(
    'Apartments per stairwell on a storey. The printed figure, or the quotient of a printed apartment count and a printed stairwell count for that storey when the per-stairwell figure is not printed. Null when those stated numbers are missing. Not a typical count.',
  ),
  elevators_per_stairwell: nullableNum(
    'Elevators per stairwell; null if unknown.',
  ),
});

const extractedApartmentAiSchema = z.object({
  label: nullableStr(
    'Printed apartment type (1 RoK, 2 RoK). Null when the row has no label.',
  ),
  count: nullableNum('Number of apartments of this type; null if unknown.'),
  area_m2: nullableNum(
    'One printed area in m². Null when the table prints a range.',
  ),
  area_min_m2: nullableNum(
    'Lower end of a printed area range; null otherwise.',
  ),
  area_max_m2: nullableNum(
    'Upper end of a printed area range; null otherwise.',
  ),
});

export const extractedOtherAreaRowScopeSchema = z.enum([
  'zone',
  'storey_total',
  'building_total',
]);

export type ExtractedOtherAreaRowScope = z.infer<
  typeof extractedOtherAreaRowScopeSchema
>;

const extractedOtherAreaAiSchema = z.object({
  label: nullableStr(
    'Printed label for an area that is not BTA, ljus BTA, BOA, LOA or BYA and is not mapped to a program type (e.g. trapphus, teknik). The printed name of the area itself, the same word the plan drawings print for it. A table group heading or category prefix before that name is not part of the label ("GROUP – BALKONG" is "BALKONG"). Null if unlabelled.',
  ),
  area_m2: nullableNum('Area in m² as printed; null if unreadable.'),
  row_scope: z
    .nullable(extractedOtherAreaRowScopeSchema)
    .describe(
      'zone when this is one printed line for one floor or one place. storey_total when it is the sum for one whole floor. building_total when the document prints it as the total for the whole building. Null when the document does not say.',
    ),
});

/**
 * Whole-volume area totals and distributions. OpenAI strict: every key
 * required, null or empty when the document does not state them.
 */
export const extractedVolumeAreasAiFields = {
  ljus_bta_m2: nullableNum('Ljus BTA in m²; null if unknown. Not BTA.'),
  boa_m2: nullableNum('BOA in m²; null if unknown.'),
  loa_m2: nullableNum('LOA in m²; null if unknown.'),
  bya_m2: nullableNum('BYA in m²; null if unknown.'),
  apartment_count: nullableNum(
    'Number of apartments when the document states a total; null if unknown.',
  ),
  apartments: z
    .array(extractedApartmentAiSchema)
    .describe(
      'One row per printed apartment type. Empty when the document has no distribution.',
    ),
  other_areas: z
    .array(extractedOtherAreaAiSchema)
    .describe(
      'Labelled areas that are not a BTA/BOA/LOA/BYA/ljus BTA total and are not mapped to a program type (e.g. trapphus, teknik). One row per kind of area. A sum over several kinds (a group total) is not a row. Empty when none.',
    ),
};

const storeyCountAiDescription =
  'Storey count of this building or part. When `storeys` is the whole stack: its rows at index 0 or above (attic and technical storeys count; below-entrance storeys, mezzanines and the roof do not). Otherwise the count the documents state. A printed count that disagrees with the rows does not replace them. Null when neither.';

const extractedBuildingPartAiSchema = z.object({
  part_name: z.string().describe('Name of the piece as printed. Never empty.'),
  storey_count: nullableNum(storeyCountAiDescription),
  ...extractedVolumeAreasAiFields,
  storeys: z
    .array(extractedStoreyAiSchema)
    .describe('This part’s own storey stack, same rules as the building.'),
});

const extractedBuildingAiSchema = z.object({
  building_instance_key: nullableStr(
    'Classifier / extractor key for this physical building. Alias keys for the same hus collapse to one row. Null if unknown.',
  ),
  building_name: nullableStr('Stamp or table name; null if unknown.'),
  storey_count: nullableNum(storeyCountAiDescription),
  parts: z
    .array(extractedBuildingPartAiSchema)
    .describe(
      'Named pieces of this one connected volume that have their own storey count or stack. A word that only names a piece does not make another building. Empty when the text names no pieces. A separate building is a volume the text presents as its own house, apart from this one.',
    ),
  bta_m2: nullableNum(
    'Whole-building BTA in m², not mörk BTA or the BTA of one use; null if unknown.',
  ),
  ...extractedVolumeAreasAiFields,
  window_ratio: nullableNum(WINDOW_RATIO_VALUE_DESCRIPTION),
  window_ratio_determined_by: z
    .enum(WINDOW_RATIO_DETERMINED_BY)
    .nullable()
    .describe(WINDOW_RATIO_DETERMINED_BY_DESCRIPTION),
  beam_span_m: nullableNum(
    'Typical beam span / spännvidd in metres; null if unknown. Do not invent a typical span.',
  ),
  slab_thickness_mm: nullableNum(
    'Slab thickness in millimetres when the document states one number. Null when it does not. Not a typcode and not a part name.',
  ),
  storeys: z
    .array(extractedStoreyAiSchema)
    .describe(
      'One row per printed plan in the upload. Do not drop a plan because another plan is missing. Empty when no plan can be read or when the storeys belong to parts.',
    ),
  residential: extractedResidentialAiSchema.describe(
    'Per-apartment residential figures and the residential form for this building. Null leaves when the document does not state them.',
  ),
  stairwells: extractedStairwellsAiSchema.describe(
    'Stairwell properties for this building. Null fields when unknown.',
  ),
});

/**
 * Shape the merge / FFU extractors emit. OpenAI strict: every key required,
 * null when unknown. One resolved value per leaf — never a candidate list.
 */
export const PRINTED_LOCATION_DESCRIPTION =
  'Place and street in the order the document prints them. Do not put the street before the place when the source writes the place first.';

export const PROPERTY_DESIGNATION_DESCRIPTION =
  'Property designation or designations of the site (fastighetsbeteckning), as printed. Null when not printed.';

export const DETAILED_PLAN_DESCRIPTION =
  'Detailed development plan (detaljplan) that applies to the site, by the name and/or number (plannummer, aktbeteckning) as printed. Null when not printed.';

export const CLIENT_DESCRIPTION =
  'Client or developer of the project (byggherre, beställare, byggaktör), as printed. Not the architect, a consultant or the contractor. Null when not printed.';

export const extractedDesignFactsAiSchema = z.object({
  project_name: nullableStr(
    'Project name as printed, including a place printed with that name. Do not shorten it to the building name. Null if unknown.',
  ),
  location: nullableStr(
    `${PRINTED_LOCATION_DESCRIPTION} Not lat/lng. Null if unknown.`,
  ),
  property_designation: nullableStr(PROPERTY_DESIGNATION_DESCRIPTION),
  detailed_plan: nullableStr(DETAILED_PLAN_DESCRIPTION),
  client: nullableStr(CLIENT_DESCRIPTION),
  storey_count: nullableNum(
    'Typical or max storey count for the project; per-building storeys[] is canonical. Null if unknown.',
  ),
  building_count: nullableNum(
    'How many volumes the text presents as their own houses. Pieces of one connected volume count as one. Null if unknown.',
  ),
  buildings: z
    .array(extractedBuildingAiSchema)
    .describe(
      'One row per volume the text presents as its own house. A name for a piece of that volume is a part on that row, not a second row. Alias stamps for the same house are one row. Empty if none can be grounded.',
    ),
});

export type ExtractedDesignFactsAi = z.infer<
  typeof extractedDesignFactsAiSchema
>;

const withOtherProgramLabel = (value: unknown): unknown => {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) {
    return value;
  }
  const row = value as Record<string, unknown>;
  if (row.type !== 'other') return value;
  const label = row.label;
  const printed =
    typeof label === 'string' && label.trim() !== '' ? label.trim() : null;
  return { ...row, label: printed };
};

const extractedMappedProgramSchema = z.object({
  type: z.enum(MAPPED_EXTRACTED_PROGRAM_TYPES),
  areaM2: z.number().nullable(),
  /** {@link EXTRACTED_PROGRAM_SHARE_NODON_DESCRIPTION} */
  share: z.number().nullable().optional(),
});

const extractedOtherProgramSchema = z.object({
  type: z.literal('other'),
  areaM2: z.number().nullable(),
  share: z.number().nullable().optional(),
  /** Printed word. Null when the document did not print one. */
  label: z.string().nullable(),
});

const extractedProgramSchema = z.preprocess(
  withOtherProgramLabel,
  z.discriminatedUnion('type', [
    extractedMappedProgramSchema,
    extractedOtherProgramSchema,
  ]),
);

export type ExtractedStoreyProgram = z.infer<typeof extractedProgramSchema>;

const legacyStoreyLevel = (
  row: Record<string, unknown>,
): z.input<typeof storeyLevelSchema> | null => {
  if (row.isGroundFloor === true) return { kind: 'single', index: 0 };
  const index = row.index;
  return typeof index === 'number' && Number.isInteger(index)
    ? { kind: 'single', index }
    : null;
};

/** Batches stored before `level` read `index` / `isGroundFloor` as a single level. */
const upgradeLegacyStorey = (value: unknown): unknown => {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) {
    return value;
  }
  const row = value as Record<string, unknown>;
  if ('level' in row) return row;
  const { index: _index, isGroundFloor: _isGroundFloor, ...rest } = row;
  return {
    ...rest,
    planNumbers: parsePlanNumbers(
      typeof row.label === 'string' ? row.label : null,
    ),
    level: legacyStoreyLevel(row),
  };
};

const extractedStoreySchema = z.preprocess(
  upgradeLegacyStorey,
  z.object({
    /** Printed name; canonical `Plan 13` / `Plan 11–14` when it prints plan numbers. */
    label: z.string().nullable(),
    /** Printed plan numbers, ascending; a range is written out. */
    planNumbers: z.array(z.number().int()).default([]),
    /** Placement from the entrance; null until an entrance is known. */
    level: storeyLevelSchema.nullable(),
    innerHeightM: z.number().nullable(),
    floorToFloorHeightM: z.number().nullable(),
    areaM2: z.number().nullable(),
    programs: z.array(extractedProgramSchema),
    elevationMarkM: z.number().nullable().default(null),
    elevationFromGroundM: z.number().nullable().default(null),
  }),
);

export type ExtractedStoreyFacts = z.infer<typeof extractedStoreySchema>;

const extractedResidentialSchema = z.object({
  livingAreaPerApartmentM2: z.number().nullable(),
  balconyAreaPerApartmentM2: z.number().nullable(),
  bathroomAreaPerApartmentM2: z.number().nullable().default(null),
  programType: z.enum(RESIDENTIAL_PROGRAM_FORM).nullable().default(null),
});

export const extractedApartmentFactsSchema = z.object({
  label: z.string().nullable(),
  count: z.number().nullable(),
  areaM2: z.number().nullable(),
  areaMinM2: z.number().nullable(),
  areaMaxM2: z.number().nullable(),
});

export type ExtractedApartmentFacts = z.infer<
  typeof extractedApartmentFactsSchema
>;

export const extractedOtherAreaFactsSchema = z.object({
  label: z.string().nullable(),
  areaM2: z.number().nullable(),
  /** What the area covers. Absent when the source did not say. */
  rowScope: extractedOtherAreaRowScopeSchema.nullable().optional(),
});

export type ExtractedOtherAreaFacts = z.infer<
  typeof extractedOtherAreaFactsSchema
>;

/** Area totals shared by a building and a part. Absent on batches stored before these fields. */
const extractedVolumeAreasSchema = {
  ljusBtaM2: z.number().nullable().default(null),
  boaM2: z.number().nullable().default(null),
  loaM2: z.number().nullable().default(null),
  byaM2: z.number().nullable().default(null),
  apartmentCount: z.number().nullable().default(null),
  apartments: z.array(extractedApartmentFactsSchema).default([]),
  otherAreas: z.array(extractedOtherAreaFactsSchema).default([]),
};

export type ExtractedResidentialFacts = z.infer<
  typeof extractedResidentialSchema
>;

const extractedStairwellsSchema = z.object({
  apartmentsPerStairwellPerStorey: z.number().nullable(),
  elevatorsPerStairwell: z.number().nullable(),
});

export type ExtractedStairwellsFacts = z.infer<
  typeof extractedStairwellsSchema
>;

const extractedBuildingPartSchema = z.object({
  name: z.string(),
  storeyCount: storeyCountSchema.nullable(),
  btaM2: z.number().nullable().default(null),
  ...extractedVolumeAreasSchema,
  storeys: z.array(extractedStoreySchema),
  /** Building key the part came from when separate houses were joined. */
  sourceBuildingKey: z.string().nullable().default(null),
});

/**
 * Connected part of one building with its own storey stack (Del A / Del B,
 * or a house joined to its neighbour over a shared garage). Storeys of a part
 * never merge with another part's storeys.
 */
export type ExtractedBuildingPartFacts = z.infer<
  typeof extractedBuildingPartSchema
>;

export const EXTRACTED_BUILDING_CONNECTION_RELATIONSHIPS = [
  'shared_base_slab',
  'connected_volume',
  'separate',
  'unknown',
] as const;

/**
 * How the houses joined into one building hang together, as the batch
 * composition step found it (`building_compositions`).
 */
const extractedBuildingConnectionSchema = z.object({
  relationship: z.enum(EXTRACTED_BUILDING_CONNECTION_RELATIONSHIPS),
  note: z.string(),
});

export type ExtractedBuildingConnection = z.infer<
  typeof extractedBuildingConnectionSchema
>;

/** Closed roof-form list. Facit comparison uses the same values. */
export const ROOF_FORM_TYPES = ['flat', 'sadel', 'pulpet', 'mansard'] as const;

export type RoofFormType = (typeof ROOF_FORM_TYPES)[number];

/** Where on a segment a value was read. Not the segment class. */
export const READ_SOURCES = ['text', 'geometry'] as const;

export type ReadSource = (typeof READ_SOURCES)[number];

/** How a value was read. `direct` is stated, not guessed. */
export const READ_FROMS = ['direct', 'estimate', 'extrapolated'] as const;

export type ReadFrom = (typeof READ_FROMS)[number];

/**
 * One value plus how it was read inside a segment. Not the segment class.
 * {@link Reading} is the same pair without the value, for a candidate leaf.
 */
export type Read<V> = {
  value: V;
  source: ReadSource;
  from: ReadFrom;
};

export type Reading = {
  source: ReadSource;
  from: ReadFrom;
};

/** Stored read. The OpenAI payload uses the same keys, each nullable. */
export const readSchema = <T extends z.ZodType>(value: T) =>
  z
    .object({
      value,
      source: z.enum(READ_SOURCES),
      from: z.enum(READ_FROMS),
    })
    .strict();

const pitchedRoofTypeSchema = z.enum(['sadel', 'pulpet', 'mansard']);

/**
 * Stored roof. `type.value` is the discriminant. Flat has no pitch.
 * A prose string stored before this shape is dropped, not parsed.
 */
export const roofFormSchema = z.union([
  z
    .object({
      type: readSchema(z.literal('flat')),
    })
    .strict(),
  z
    .object({
      type: readSchema(pitchedRoofTypeSchema),
      pitchDeg: readSchema(z.number()).nullable(),
    })
    .strict(),
]);

export type RoofForm = z.infer<typeof roofFormSchema>;

const roofFormField = z.preprocess(
  (value) => (typeof value === 'string' ? null : value),
  roofFormSchema.nullable().default(null),
);

const extractedBuildingSchema = z.object({
  buildingInstanceKey: z.string().nullable(),
  buildingName: z.string().nullable(),
  /** As the documents state it; never derived from {@link storeys}. */
  storeyCount: storeyCountSchema.nullable().default(null),
  btaM2: z.number().nullable(),
  ...extractedVolumeAreasSchema,
  windowRatio: z.number().nullable(),
  /**
   * False when {@link windowRatio} is printed or calculated.
   * True when it was estimated from a facade, elevation, or rendering.
   * Null when {@link windowRatio} is null, or on rows stored before the flag.
   */
  windowRatioEstimated: z.boolean().nullable().default(null),
  /**
   * `read`, `calculated`, or `estimated`. Null when {@link windowRatio} is
   * null, or on rows stored before the field. The boolean flag cannot say
   * `calculated`.
   */
  windowRatioDeterminedBy: z
    .enum(WINDOW_RATIO_DETERMINED_BY)
    .nullable()
    .default(null),
  beamSpanM: z.number().nullable(),
  /**
   * Slab thickness in millimetres when a step already read a number.
   * Null when none was stated, or on rows stored before the field.
   */
  slabThicknessMm: z.number().nullable().default(null),
  /**
   * Roof form the model read, with how each field was read. Null when no
   * drawing showed a form. A legacy prose string is dropped, not parsed.
   */
  roofForm: roofFormField,
  /** Storeys of a single-volume building. Empty when {@link parts} carry them. */
  storeys: z.array(extractedStoreySchema),
  /** Empty when the building is one volume. */
  parts: z.array(extractedBuildingPartSchema).default([]),
  /** Set when {@link parts} are houses joined into this building. */
  connection: extractedBuildingConnectionSchema.nullable().default(null),
  residential: extractedResidentialSchema.nullable(),
  stairwells: extractedStairwellsSchema.nullable(),
});

export type ExtractedBuildingFacts = z.infer<typeof extractedBuildingSchema>;

/** Persisted camelCase tree on `aggregatedOutput.extractedDesignFacts`. */
export const extractedDesignFactsSchema = z.object({
  projectName: z.string().nullable(),
  location: z.string().nullable(),
  /** Null when none was printed, or on rows stored before the field. */
  propertyDesignation: z.string().nullable().default(null),
  /** Null when none was printed, or on rows stored before the field. */
  detailedPlan: z.string().nullable().default(null),
  /** Null when none was printed, or on rows stored before the field. */
  client: z.string().nullable().default(null),
  storeyCount: z.number().nullable(),
  buildingCount: z.number().nullable(),
  buildings: z.array(extractedBuildingSchema),
});

export type ExtractedDesignFacts = z.infer<typeof extractedDesignFactsSchema>;

/**
 * Printed totals for a body that is not one row of
 * {@link ExtractedDesignFacts.buildings}: a composition, or the project.
 */
export const extractedAreaTotalsSchema = z.object({
  btaM2: z.number().nullable(),
  ljusBtaM2: z.number().nullable(),
  boaM2: z.number().nullable(),
  loaM2: z.number().nullable(),
  byaM2: z.number().nullable(),
});

export type ExtractedAreaTotals = z.infer<typeof extractedAreaTotalsSchema>;

export const extractedDesignFactConflictRejectedSchema = z.object({
  value: z.union([z.string(), z.number()]),
  source: extractedDesignFactSourceSchema,
  fileName: z.string(),
});

/**
 * Admin sidecar: why a leaf was chosen. Not part of the programmatic facts tree.
 */
export const extractedDesignFactConflictSchema = z.object({
  path: z.string(),
  picked: z.union([z.string(), z.number(), z.null()]),
  rejected: z.array(extractedDesignFactConflictRejectedSchema),
});

export type ExtractedDesignFactConflict = z.infer<
  typeof extractedDesignFactConflictSchema
>;

/** Merge-input candidate. Not stored on the facts tree. */
export type ExtractedDesignFactCandidate = {
  path: string;
  kind: ExtractedDesignFactLeafKind;
  value: string | number;
  source: ExtractedDesignFactSource;
  fileName: string;
  confidence: number | null;
  sheetNote: string | null;
  /**
   * Set on `window_ratio` only. False is printed or calculated.
   * True is an estimate from a facade, elevation, or rendering.
   */
  windowRatioEstimated?: boolean | null;
  /** Set on `window_ratio` only. Absent on candidates stored before the field. */
  windowRatioDeterminedBy?: WindowRatioDeterminedBy | null;
  /** Set on `roof_form` only. How that field was read inside the segment. */
  roofReading?: Reading;
  /**
   * Building ids an area-schedule row was printed for, when it covers two or
   * more. The path then has no single building (`buildings[_]`).
   */
  coversBuildingKeys?: readonly string[];
  /** Set on a typical plan's area only. */
  typicalAreaScope?: TypicalAreaScope;
};

/**
 * Whether the area printed for a typical plan is the area of each storey it
 * stands for, or the sum of those storeys.
 */
export type TypicalAreaScope = 'per_storey' | 'sum';

export const emptyExtractedStoreyFacts = (): ExtractedStoreyFacts => ({
  label: null,
  planNumbers: [],
  level: null,
  innerHeightM: null,
  floorToFloorHeightM: null,
  areaM2: null,
  programs: [],
  elevationMarkM: null,
  elevationFromGroundM: null,
});

export type ExtractedVolumeAreas = Pick<
  ExtractedBuildingFacts,
  | 'ljusBtaM2'
  | 'boaM2'
  | 'loaM2'
  | 'byaM2'
  | 'apartmentCount'
  | 'apartments'
  | 'otherAreas'
>;

export const emptyExtractedVolumeAreas = (): ExtractedVolumeAreas => ({
  ljusBtaM2: null,
  boaM2: null,
  loaM2: null,
  byaM2: null,
  apartmentCount: null,
  apartments: [],
  otherAreas: [],
});

export const emptyExtractedBuildingPartFacts =
  (): ExtractedBuildingPartFacts => ({
    name: '',
    storeyCount: null,
    btaM2: null,
    ...emptyExtractedVolumeAreas(),
    storeys: [],
    sourceBuildingKey: null,
  });

export const emptyExtractedBuildingFacts = (): ExtractedBuildingFacts => ({
  buildingInstanceKey: null,
  buildingName: null,
  storeyCount: null,
  btaM2: null,
  ...emptyExtractedVolumeAreas(),
  windowRatio: null,
  windowRatioEstimated: null,
  windowRatioDeterminedBy: null,
  beamSpanM: null,
  slabThicknessMm: null,
  roofForm: null,
  storeys: [],
  parts: [],
  connection: null,
  residential: null,
  stairwells: null,
});

export const emptyExtractedDesignFacts = (): ExtractedDesignFacts => ({
  projectName: null,
  location: null,
  propertyDesignation: null,
  detailedPlan: null,
  client: null,
  storeyCount: null,
  buildingCount: null,
  buildings: [],
});

export const emptyExtractedVolumeAreasAi = () => ({
  ljus_bta_m2: null,
  boa_m2: null,
  loa_m2: null,
  bya_m2: null,
  apartment_count: null,
  apartments: [] as ExtractedDesignFactsAi['buildings'][number]['apartments'],
  other_areas: [] as ExtractedDesignFactsAi['buildings'][number]['other_areas'],
});

export const emptyExtractedDesignFactsAi = (): ExtractedDesignFactsAi => ({
  project_name: null,
  location: null,
  property_designation: null,
  detailed_plan: null,
  client: null,
  storey_count: null,
  building_count: null,
  buildings: [],
});
